/**
 * Playable interface
 *
 * Implemented by behaviors that can be driven by a Timeline.
 * The Timeline discovers Playable behaviors in the scene and
 * calls these methods directly — no event bus needed.
 *
 * Contract:
 * - play()  — Resume from current position. Must NOT reset to the start.
 * - pause() — Freeze at current position. Visual state stays as-is.
 * - stop()  — Halt playback and reset to initial state (time 0).
 * - seek()  — Jump to a specific time. Does not start or stop playback.
 */
export interface Playable {
    /** Start or resume playback from the current position. */
    play(): void | Promise<void>;
    /** Pause playback, freezing at the current position. */
    pause(): void;
    /** Stop playback and reset to initial state (time 0). */
    stop(): void;
    /** Seek to a specific time in seconds. Does not affect play/pause state. */
    seek(time: number): void;
}

/**
 * Optional capability: swap the media source URL at runtime.
 * Implemented by behaviors backed by an HTMLMediaElement (Video, AudioClip).
 * Animation and Pages do not implement this.
 */
export interface PlayableSource {
    /**
     * Replace the underlying media URL. Implementations should pause and reset
     * the current source, then begin loading the new one. Idempotent: if the
     * URL matches the current source, returns without reloading.
     */
    setSource(url: string): void | Promise<void>;
}

/** Optional capability: set element/audio volume on a 0–1 scale. */
export interface PlayableVolume {
    setVolume(volume: number): void;
}

/**
 * Optional capability: read whether playback is currently running.
 * Video/AudioClip read the underlying HTMLMediaElement (playing = not paused
 * and not ended); Animation, Timeline, and AnimationSequence already expose
 * `isPlaying()` and satisfy this structurally.
 */
export interface PlayableState {
    /** Whether playback is currently running. */
    isPlaying(): boolean;
}

/**
 * Optional capability: report a media clock a Timeline can follow
 * (RECORDINGS_SPEC §4.2). A Timeline whose `clock` names a node carrying a
 * PlayableClock treats that medium as its master clock while playing.
 *
 * `getClockTime()` returns the medium's current position in the OWNING
 * Timeline's time base (seconds — e.g. `element.currentTime + startAt` for
 * AudioClip), or `null` whenever the medium is not producing time:
 *
 * - no medium loaded (no element / no metadata);
 * - paused or ended — including paused because the Timeline itself paused
 *   (the Timeline is not advancing then, so the value would be unused), and
 *   paused because the autoplay policy refused `play()`;
 * - seeking, stalled or waiting for data (`readyState < HAVE_FUTURE_DATA`);
 * - positioned before its own start (AudioClip `startAt`).
 *
 * `null` is not an error: the Timeline advances on scene time instead and
 * snaps back to the medium once it produces time again.
 */
export interface PlayableClock {
    /** Position in the owning Timeline's time base (s), or null when not producing time. */
    getClockTime(): number | null;
}

/**
 * Shared {@link PlayableClock} read for HTMLMediaElement-backed Playables:
 * `el.currentTime + offset` while the element is actually producing time,
 * otherwise `null` (see PlayableClock for the list).
 * @internal
 */
export function mediaElementClockTime(el: HTMLMediaElement | null | undefined, offset = 0): number | null {
    if (!el) return null;
    if (el.paused || el.ended || el.seeking) return null;
    if (el.readyState < 3 /* HAVE_FUTURE_DATA */) return null;
    const t = el.currentTime;
    return Number.isFinite(t) ? t + offset : null;
}

/**
 * Optional capability: the Playable's state is a pure function of time — a
 * keyframe clip — so a Timeline can SAMPLE it instead of letting it run.
 *
 * A Timeline that OWNS a sampled Playable calls `sampleAt(t)` with its own
 * time `t` every frame it advances, before animations are evaluated — never
 * `play()`. So the Playable shows exactly the Timeline's time on every
 * rendered frame: it cannot drift from the Timeline (and, through a master
 * clock, from the voice), a seek is visible in the frame it happens in, and
 * the Timeline's `speed` applies to it.
 *
 * Ownership:
 * - `sampleAt(t)` CLAIMS: the Timeline calls it on play from stopped, seek
 *   and loop; it ends whatever session the Playable was running and leaves
 *   it at `t`, not running on a clock of its own;
 * - anything else that drives the Playable (its own play(), seek(), stop())
 *   ENDS the claim, and `isSampling()` turns false;
 * - per frame, a Timeline samples only Playables whose `isSampling()` is
 *   true. A clip something else started (a graph's `trace/playAnimation`)
 *   runs untouched until the Timeline next claims it.
 *
 * `t` is in the Timeline's time base; mapping to the Playable's own time
 * (speed, wrap) is the Playable's business. For a keyframe clip, Timeline
 * time IS the clip's own time from 0 — no lead-in trimming (RECORDINGS_SPEC
 * §4.2). It is cheap enough to call every frame.
 *
 * Media (AudioClip, Video) are not sampled: seeking a media element every
 * frame would stutter. They run on their own clock and one of them may be
 * the master clock (PlayableClock).
 */
export interface PlayableSampled {
    /** Claim for the Timeline and put the Playable at Timeline time `time` (s), not running. */
    sampleAt(time: number): void;
    /** Whether the claim made by the last `sampleAt` is still current. */
    isSampling(): boolean;
}

/** Optional capability: read element/audio volume on a 0–1 scale. */
export interface PlayableVolumeReader {
    /** Current volume (0–1). Implementations return 1 when no medium is loaded
     *  (the HTMLMediaElement default), so "no element yet" reads as unmuted. */
    getVolume(): number;
}

/** Type guard for checking if a behavior implements Playable */
export function isPlayable(obj: unknown): obj is Playable {
    return (
        obj !== null &&
        typeof obj === 'object' &&
        typeof (obj as Playable).play === 'function' &&
        typeof (obj as Playable).pause === 'function' &&
        typeof (obj as Playable).stop === 'function' &&
        typeof (obj as Playable).seek === 'function'
    );
}

/** Type guard for the optional PlayableSource capability. */
export function hasPlayableSource(obj: unknown): obj is PlayableSource {
    return (
        obj !== null &&
        typeof obj === 'object' &&
        typeof (obj as PlayableSource).setSource === 'function'
    );
}

/** Type guard for the optional PlayableVolume capability. */
export function hasPlayableVolume(obj: unknown): obj is PlayableVolume {
    return (
        obj !== null &&
        typeof obj === 'object' &&
        typeof (obj as PlayableVolume).setVolume === 'function'
    );
}

/** Type guard for the optional PlayableState capability. */
export function hasPlayableState(obj: unknown): obj is PlayableState {
    return (
        obj !== null &&
        typeof obj === 'object' &&
        typeof (obj as PlayableState).isPlaying === 'function'
    );
}

/** Type guard for the optional PlayableVolumeReader capability. */
export function hasPlayableVolumeReader(obj: unknown): obj is PlayableVolumeReader {
    return (
        obj !== null &&
        typeof obj === 'object' &&
        typeof (obj as PlayableVolumeReader).getVolume === 'function'
    );
}

/** Type guard for the optional PlayableClock capability. */
export function hasPlayableClock(obj: unknown): obj is PlayableClock {
    return (
        obj !== null &&
        typeof obj === 'object' &&
        typeof (obj as PlayableClock).getClockTime === 'function'
    );
}

/** Type guard for the optional PlayableSampled capability. */
export function hasPlayableSampled(obj: unknown): obj is PlayableSampled {
    return (
        obj !== null &&
        typeof obj === 'object' &&
        typeof (obj as PlayableSampled).sampleAt === 'function' &&
        typeof (obj as PlayableSampled).isSampling === 'function'
    );
}
